import React from "react";
import { CheckCircle2 } from "lucide-react";

/**
 * TestSidebar
 * Sticky question navigator grouped by section. Each question renders as a
 * small node (the "progress rail") that's hollow when unanswered, filled
 * violet when answered, and solid ink + pulsing when it's the current one.
 * Clicking a node jumps straight to that question.
 *
 * Collapses to an off-canvas drawer on mobile (controlled by `open`/`onClose`
 * from the parent, toggled via the header's hamburger button).
 */
const TestSidebar = ({
  sections,
  flatQuestions,
  currentFlatIndex,
  answeredQuestionIds,
  bookmarkedIds,
  onJumpTo,
  open,
  onClose,
  totalQuestions,
  answeredCount,
}) => {
  const remaining = totalQuestions - answeredCount;

  return (
    <>
      {open && <div className="sidebar-scrim" onClick={onClose} />}
      <nav
        className={`test-sidebar ${open ? "open" : ""}`}
        aria-label="Question navigator"
      >
        <div className="sidebar-summary">
          <div className="summary-stat">
            <strong>{answeredCount}</strong>
            <span>Answered</span>
          </div>
          <div className="summary-stat">
            <strong>{remaining}</strong>
            <span>Remaining</span>
          </div>
          <div className="summary-stat">
            <strong>{totalQuestions}</strong>
            <span>Total</span>
          </div>
        </div>

        {sections.map((section, sectionIndex) => {
          const sectionQuestions = flatQuestions.filter(
            (fq) => fq.sectionIndex === sectionIndex,
          );
          const sectionAnswered = sectionQuestions.filter((fq) =>
            answeredQuestionIds.has(fq.question.questionId),
          ).length;
          const sectionComplete =
            sectionAnswered === sectionQuestions.length &&
            sectionQuestions.length > 0;

          return (
            <div className="sidebar-section" key={sectionIndex}>
              <div className="sidebar-section-title">
                {sectionComplete && <CheckCircle2 className="section-check" />}
                <span className="section-name">
                  {sectionIndex + 1}. {section.sectionTitle}
                </span>
                <span className="section-count">
                  {sectionAnswered}/{sectionQuestions.length}
                </span>
              </div>

              <div className="sidebar-question-grid">
                {sectionQuestions.map((fq) => {
                  const flatIndex = flatQuestions.indexOf(fq);
                  const isAnswered = answeredQuestionIds.has(
                    fq.question.questionId,
                  );
                  const isCurrent = flatIndex === currentFlatIndex;
                  const isBookmarked = bookmarkedIds.has(
                    fq.question.questionId,
                  );

                  return (
                    <button
                      key={fq.question.questionId}
                      type="button"
                      className={[
                        "q-node",
                        isAnswered ? "answered" : "",
                        isCurrent ? "current" : "",
                        isBookmarked ? "bookmarked" : "",
                      ]
                        .join(" ")
                        .trim()}
                      onClick={() => onJumpTo(flatIndex)}
                      aria-current={isCurrent ? "true" : undefined}
                      aria-label={`Question ${fq.questionIndexInSection + 1} of ${section.sectionTitle}${isAnswered ? ", answered" : ", unanswered"}`}
                    >
                      {fq.questionIndexInSection + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    </>
  );
};

export default TestSidebar;
